import * as queryString from 'query-string';
import { useEffect, useReducer, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { unsetQueryParam } from "./UrlUtils";

function promiseReducer(state, action) {
  switch (action.type) {
    case 'pending':
      return {
        pending: true,
        result: null,
        error: null,
        refresh: null
      };

    case 'fulfilled':
      return {
        pending: false,
        result: action.payload,
        refresh: action.refresh
      };

    default:
      return {
        pending: false,
        error: action.payload,
        refresh: action.refresh
      };
  }
}

export function usePromise(promiseFn, memoizeOn = [], runPromise = true) {
  const initialState = {
    result: null,
    error: null,
    pending: true,
    refresh: null
  };
  const [state, dispatch] = useReducer(promiseReducer, initialState);

  const promiseExecuter = () => {
    dispatch({
      type: 'pending'
    });
    promiseFn().then(result => dispatch({
      type: 'fulfilled',
      payload: result,
      refresh: promiseExecuter
    })).catch(error => dispatch({
      type: 'error',
      payload: error,
      refresh: promiseExecuter
    }));
  };

  useEffect(() => {
    if (runPromise) {
      promiseExecuter();
    }
  }, memoizeOn);
  return state;
}
/**
 * Get parsed URL query params
 */

export function useQueryParams() {
  const {
    search
  } = useLocation();
  return queryString.parse(search);
}
/**
 * This hook will look for a query param in the url.
 * If present, it will swallow (remove) the query param
 * and its value from the browser URL.
 * It returns the current value of the query param, if present
 */

export function useSwallowQueryParam(queryParam) {
  const queryParams = useQueryParams();
  const history = useHistory();
  const currentPath = window.location.href;
  const updatedPath = unsetQueryParam(window.location.href, queryParam);

  if (!currentPath.endsWith(updatedPath)) {
    history.replace(updatedPath);
  }

  return queryParams[queryParam];
}
/**
 * In functional components, it isn't possible to get the previous
 * value of a state / prop directly (something that was possible
 * using lifecycle methods of class components). This hook provides
 * a lagging version of the value passed to it.
 */

export function usePrevious(value) {
  // $FlowFixMe Flow doesn't like empty args to `useRef` when using generics
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
} // eslint-disable-next-line flowtype/no-weak-types

/**
 * Validates values of query parameters for a route, and
 * sets defaults if validations fail
 */
export const useValidateQueryParams = paramValidation => {
  const {
    search: oldSearch,
    pathname
  } = useLocation();
  const queryParams = queryString.parse(oldSearch);
  const updatedQueryParams = { ...queryParams
  };
  let hasInvalidOrMissingParams = false;
  Object.keys(paramValidation).forEach(key => {
    const {
      validate,
      defaultValue
    } = paramValidation[key];
    const originalValue = queryParams[key];
    const isMissingValue = typeof originalValue === 'undefined' && typeof defaultValue !== 'undefined';
    const isValidValue = validate ? validate(originalValue) : true;

    if (isMissingValue || !isValidValue) {
      hasInvalidOrMissingParams = true;
      updatedQueryParams[key] = defaultValue;
    }
  });
  const updatedQueryString = queryString.stringify(updatedQueryParams);
  const updatedSearch = updatedQueryString ? "?".concat(updatedQueryString) : '';
  const redirectRoute = hasInvalidOrMissingParams ? pathname + updatedSearch : null;
  return {
    redirectRoute,
    queryParams: updatedQueryParams
  };
};